import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";
import { STATUS_OPTIONS } from "@/components/StatusBadge";
import Attachments from "@/components/Attachments";
import DatePicker from "@/components/DatePicker";
import ClientFormDialog from "@/components/ClientFormDialog";
import UserFormDialog from "@/components/UserFormDialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import {
    Loader2,
    Repeat,
    Check,
    ChevronsUpDown,
    Plus,
    Paperclip,
    UploadCloud,
    X,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITY_OPTIONS = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
];

const RECURRENCE_OPTIONS = [
    { value: "none", label: "One-time (no recurrence)" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly (e.g. GST)" },
    { value: "quarterly", label: "Quarterly (e.g. TDS)" },
    { value: "yearly", label: "Yearly (e.g. ITR)" },
];

const empty = {
    client_name: "",
    assigned_to: "",
    assigned_to_name: "",
    work_type: "",
    task_date: new Date().toISOString().slice(0, 10),
    deadline_date: "",
    status: "not_initiated",
    priority: "medium",
    remarks: "",
    billing_status: false,
    reimbursable_expense: false,
    reimbursable_amount: "",
    reimbursable_description: "",
    approved: false,
    recurrence: "none",
};

export default function TaskFormDialog({ open, onOpenChange, task, onSaved }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);
    const [clients, setClients] = useState([]);
    const [clientOpen, setClientOpen] = useState(false);
    const [clientQuery, setClientQuery] = useState("");
    const [showAddClient, setShowAddClient] = useState(false);
    const [users, setUsers] = useState([]);
    const [assignedOpen, setAssignedOpen] = useState(false);
    const [assignedQuery, setAssignedQuery] = useState("");
    const [showAddUser, setShowAddUser] = useState(false);
    const [pendingFiles, setPendingFiles] = useState([]); // staged during create
    const pendingRef = useRef(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (open) {
            setPendingFiles([]);
            api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
            api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
            setForm(
                task
                    ? {
                          ...empty,
                          ...task,
                          deadline_date: task.deadline_date || "",
                          remarks: task.remarks || "",
                          reimbursable_amount: task.reimbursable_amount ?? "",
                          reimbursable_description: task.reimbursable_description || "",
                          recurrence: task.recurrence || "none",
                      }
                    : empty,
            );
        }
    }, [open, task]);

    const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onClientCreated = (client) => {
        if (!client?.name) return;
        setClients((prev) =>
            prev.some((c) => c.id === client.id) ? prev : [...prev, client],
        );
        change("client_name", client.name);
        setClientQuery("");
    };

    const filteredClients = clients.filter((c) =>
        c.name.toLowerCase().includes(clientQuery.trim().toLowerCase()),
    );

    const onUserCreated = (u) => {
        if (!u?.id) return;
        setUsers((prev) => (prev.some((x) => x.id === u.id) ? prev : [...prev, u]));
        setForm((f) => ({ ...f, assigned_to: u.id, assigned_to_name: u.name }));
        setAssignedQuery("");
    };

    const filteredUsers = users.filter((u) => {
        const q = assignedQuery.trim().toLowerCase();
        return (
            (u.name || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q)
        );
    });

    const selectedAssigneeName =
        users.find((u) => u.id === form.assigned_to)?.name ||
        form.assigned_to_name ||
        "";

    const addPendingFiles = (e) => {
        const files = Array.from(e.target.files || []);
        const valid = files.filter((f) => {
            if (f.size > 10 * 1024 * 1024) {
                toast.error(`"${f.name}" exceeds 10MB`);
                return false;
            }
            return true;
        });
        setPendingFiles((prev) => [...prev, ...valid]);
        if (pendingRef.current) pendingRef.current.value = "";
    };

    const removePending = (idx) =>
        setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

    const onDeleteTask = async () => {
        if (!task?.id) return;
        try {
            await api.delete(`/tasks/${task.id}`);
            toast.success("Task deleted");
            setConfirmDelete(false);
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.client_name?.trim() || !form.work_type?.trim() || !form.task_date) {
            toast.error("Client name, type of work and start date are required");
            return;
        }
        if (!form.deadline_date) {
            toast.error("Due date is required");
            return;
        }
        if (!form.assigned_to) {
            toast.error("Please choose who the task is assigned to");
            return;
        }
        if (
            form.reimbursable_expense &&
            (form.reimbursable_amount === "" ||
                form.reimbursable_amount == null ||
                Number(form.reimbursable_amount) <= 0)
        ) {
            toast.error("Enter the reimbursable amount");
            return;
        }
        setSubmitting(true);
        const payload = {
            ...form,
            client_name: form.client_name.trim(),
            work_type: form.work_type.trim(),
            deadline_date: form.deadline_date || null,
            remarks: form.remarks || null,
            reimbursable_amount:
                form.reimbursable_amount === "" || form.reimbursable_amount == null
                    ? null
                    : Number(form.reimbursable_amount),
            reimbursable_description: form.reimbursable_description || null,
        };
        try {
            if (task?.id) {
                await api.patch(`/tasks/${task.id}`, { ...payload, if_match: task.updated_at });
                toast.success("Task updated");
            } else {
                const { data: created } = await api.post("/tasks", payload);
                // Upload any files staged during creation now that we have a task id.
                for (const f of pendingFiles) {
                    try {
                        const fd = new FormData();
                        fd.append("file", f);
                        await api.post(`/tasks/${created.id}/attachments`, fd, {
                            headers: { "Content-Type": "multipart/form-data" },
                        });
                    } catch (e) {
                        toast.error(`Attachment "${f.name}": ${formatApiError(e)}`);
                    }
                }
                toast.success("Task created");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
                        {task ? "Edit task" : "New task"}
                    </DialogTitle>
                    <DialogDescription>
                        Fields marked with <span className="text-primary font-semibold">*</span> are mandatory.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} className="space-y-5">
                    {/* Mandatory */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="client_name">
                                Client name <span className="text-primary">*</span>
                            </Label>
                            <Popover open={clientOpen} onOpenChange={setClientOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={clientOpen}
                                        className="w-full justify-between font-normal"
                                        data-testid="task-client-trigger"
                                    >
                                        <span className={form.client_name ? "" : "text-muted-foreground"}>
                                            {form.client_name || "Select or add a client"}
                                        </span>
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-[--radix-popover-trigger-width] p-0"
                                    align="start"
                                >
                                    <Command shouldFilter={false}>
                                        <CommandInput
                                            placeholder="Search client..."
                                            value={clientQuery}
                                            onValueChange={setClientQuery}
                                            data-testid="task-client-search"
                                        />
                                        <CommandList>
                                            {filteredClients.length === 0 && (
                                                <CommandEmpty>No client found.</CommandEmpty>
                                            )}
                                            <CommandGroup>
                                                {filteredClients.map((c) => (
                                                    <CommandItem
                                                        key={c.id}
                                                        value={c.name}
                                                        onSelect={() => {
                                                            change("client_name", c.name);
                                                            setClientOpen(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                form.client_name === c.name
                                                                    ? "opacity-100"
                                                                    : "opacity-0",
                                                            )}
                                                        />
                                                        {c.name}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                            <CommandSeparator />
                                            <CommandGroup>
                                                <CommandItem
                                                    value="__add_new_client__"
                                                    onSelect={() => {
                                                        setClientOpen(false);
                                                        setShowAddClient(true);
                                                    }}
                                                    className="text-primary"
                                                    data-testid="task-add-client"
                                                >
                                                    <Plus className="mr-2 h-4 w-4" />
                                                    {clientQuery.trim()
                                                        ? `Add "${clientQuery.trim()}" as a new client`
                                                        : "Add a new client"}
                                                </CommandItem>
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label>
                                Assigned to <span className="text-primary">*</span>
                            </Label>
                            <Popover open={assignedOpen} onOpenChange={setAssignedOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={assignedOpen}
                                        className="w-full justify-between font-normal"
                                        data-testid="task-assigned-trigger"
                                    >
                                        <span className={selectedAssigneeName ? "" : "text-muted-foreground"}>
                                            {selectedAssigneeName || "Select a user"}
                                        </span>
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-[--radix-popover-trigger-width] p-0"
                                    align="start"
                                >
                                    <Command shouldFilter={false}>
                                        <CommandInput
                                            placeholder="Search user by name or email..."
                                            value={assignedQuery}
                                            onValueChange={setAssignedQuery}
                                            data-testid="task-assigned-search"
                                        />
                                        <CommandList>
                                            {filteredUsers.length === 0 && (
                                                <CommandEmpty>No user found.</CommandEmpty>
                                            )}
                                            <CommandGroup>
                                                {filteredUsers.map((u) => (
                                                    <CommandItem
                                                        key={u.id}
                                                        value={u.id}
                                                        onSelect={() => {
                                                            setForm((f) => ({
                                                                ...f,
                                                                assigned_to: u.id,
                                                                assigned_to_name: u.name,
                                                            }));
                                                            setAssignedOpen(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                form.assigned_to === u.id
                                                                    ? "opacity-100"
                                                                    : "opacity-0",
                                                            )}
                                                        />
                                                        <span className="flex flex-col">
                                                            <span>{u.name}</span>
                                                            <span className="text-xs text-muted-foreground">
                                                                {u.email}
                                                                {u.role === "admin" ? " · admin" : ""}
                                                            </span>
                                                        </span>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                            {isAdmin && (
                                                <>
                                                    <CommandSeparator />
                                                    <CommandGroup>
                                                        <CommandItem
                                                            value="__add_new_user__"
                                                            onSelect={() => {
                                                                setAssignedOpen(false);
                                                                setShowAddUser(true);
                                                            }}
                                                            className="text-primary"
                                                            data-testid="task-add-user"
                                                        >
                                                            <Plus className="mr-2 h-4 w-4" />
                                                            Add a new user
                                                        </CommandItem>
                                                    </CommandGroup>
                                                </>
                                            )}
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="work_type">
                                Type of work <span className="text-primary">*</span>
                            </Label>
                            <Input
                                id="work_type"
                                value={form.work_type}
                                onChange={(e) => change("work_type", e.target.value)}
                                required
                                placeholder="GST Filing, Audit, ITR..."
                                data-testid="task-worktype-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="task_date">
                                Start date <span className="text-primary">*</span>
                            </Label>
                            <DatePicker
                                value={form.task_date}
                                onChange={(v) => change("task_date", v)}
                                placeholder="Select start date"
                                testId="task-date-input"
                            />
                        </div>
                    </div>

                    {/* Optional */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="deadline_date">
                                Due date <span className="text-primary">*</span>
                            </Label>
                            <DatePicker
                                value={form.deadline_date}
                                onChange={(v) => change("deadline_date", v)}
                                placeholder="Select due date"
                                testId="task-deadline-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Status</Label>
                            <Select
                                value={form.status}
                                onValueChange={(v) => change("status", v)}
                            >
                                <SelectTrigger data-testid="task-status-trigger">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUS_OPTIONS.map((s) => (
                                        <SelectItem key={s.value} value={s.value}>
                                            <span
                                                className={
                                                    s.value === "completed"
                                                        ? "text-emerald-700"
                                                        : s.value === "in_progress"
                                                          ? "text-amber-700"
                                                          : s.value === "not_initiated"
                                                            ? "text-red-700"
                                                            : "text-blue-700"
                                                }
                                            >
                                                {s.label}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Priority</Label>
                            <Select
                                value={form.priority}
                                onValueChange={(v) => change("priority", v)}
                            >
                                <SelectTrigger data-testid="task-priority-trigger">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PRIORITY_OPTIONS.map((p) => (
                                        <SelectItem key={p.value} value={p.value}>
                                            {p.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label className="flex items-center gap-1.5">
                                <Repeat className="h-3.5 w-3.5 text-primary" />
                                Recurrence
                            </Label>
                            <Select
                                value={form.recurrence}
                                onValueChange={(v) => change("recurrence", v)}
                            >
                                <SelectTrigger data-testid="task-recurrence-trigger">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {RECURRENCE_OPTIONS.map((r) => (
                                        <SelectItem key={r.value} value={r.value}>
                                            {r.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {form.recurrence !== "none" && (
                                <p className="text-[11px] text-muted-foreground">
                                    A fresh task will be auto-created when this one is marked
                                    completed.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="remarks">Description</Label>
                        <Textarea
                            id="remarks"
                            rows={3}
                            value={form.remarks}
                            onChange={(e) => change("remarks", e.target.value)}
                            placeholder="Scope, client expectations, internal notes..."
                            data-testid="task-remarks-input"
                        />
                    </div>

                    {/* Attachments — alongside the description (staged on create) */}
                    {task?.id ? (
                        <Attachments taskId={task.id} />
                    ) : (
                        <div className="rounded-lg border border-border bg-card p-4" data-testid="attachments-staged">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Paperclip className="h-4 w-4 text-primary" />
                                    <span className="text-sm font-semibold">Attachments</span>
                                    {pendingFiles.length > 0 && (
                                        <span className="text-xs text-muted-foreground">
                                            ({pendingFiles.length})
                                        </span>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => pendingRef.current?.click()}
                                    data-testid="attachments-staged-add"
                                >
                                    <UploadCloud className="mr-1.5 h-4 w-4" /> Add files
                                </Button>
                                <input
                                    ref={pendingRef}
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={addPendingFiles}
                                    accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls,.docx,.doc,.txt,.zip"
                                />
                            </div>
                            {pendingFiles.length === 0 ? (
                                <p className="mt-3 text-xs text-muted-foreground">
                                    Attach files now — they upload automatically when the task is
                                    created (max 10MB each).
                                </p>
                            ) : (
                                <ul className="mt-3 divide-y divide-border">
                                    {pendingFiles.map((f, i) => (
                                        <li
                                            key={`${f.name}-${i}`}
                                            className="flex items-center justify-between gap-3 py-2"
                                        >
                                            <div className="truncate text-sm font-medium">
                                                {f.name}
                                            </div>
                                            <Button
                                                type="button"
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => removePending(i)}
                                            >
                                                <X className="h-4 w-4 text-red-600" />
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {/* Reimbursable */}
                    {form.reimbursable_expense && (
                        <div className="grid grid-cols-1 gap-4 rounded-lg border border-primary/20 bg-primary/5 p-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="reimb_amt">
                                    Reimbursable amount (INR){" "}
                                    <span className="text-primary">*</span>
                                </Label>
                                <Input
                                    id="reimb_amt"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.reimbursable_amount}
                                    onChange={(e) =>
                                        change("reimbursable_amount", e.target.value)
                                    }
                                    placeholder="0.00"
                                    data-testid="task-reimb-amount-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="reimb_desc">Description</Label>
                                <Input
                                    id="reimb_desc"
                                    value={form.reimbursable_description}
                                    onChange={(e) =>
                                        change("reimbursable_description", e.target.value)
                                    }
                                    placeholder="Travel to ROC, courier..."
                                    data-testid="task-reimb-desc-input"
                                />
                            </div>
                        </div>
                    )}

                    {/* Checkboxes */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label className="flex items-start gap-3 rounded-lg border border-border bg-card p-3 hover:bg-secondary/40 cursor-pointer">
                            <Checkbox
                                checked={form.reimbursable_expense}
                                onCheckedChange={(v) => change("reimbursable_expense", !!v)}
                                data-testid="task-reimbursable-checkbox"
                            />
                            <div>
                                <div className="text-sm font-semibold">Reimbursable expense</div>
                                <div className="text-xs text-muted-foreground">
                                    Has expenses claimable from client.
                                </div>
                            </div>
                        </label>
                        <label className="flex items-start gap-3 rounded-lg border border-border bg-card p-3 hover:bg-secondary/40 cursor-pointer">
                            <Checkbox
                                checked={form.approved}
                                onCheckedChange={(v) => change("approved", !!v)}
                                data-testid="task-approved-checkbox"
                            />
                            <div>
                                <div className="text-sm font-semibold">Approved</div>
                                <div className="text-xs text-muted-foreground">
                                    Client / partner has approved.
                                </div>
                            </div>
                        </label>
                    </div>

                    <DialogFooter className="sm:justify-between">
                        {task?.id ? (
                            <Button
                                type="button"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                onClick={() => setConfirmDelete(true)}
                                data-testid="task-delete-button"
                            >
                                <Trash2 className="mr-1.5 h-4 w-4" /> Delete task
                            </Button>
                        ) : (
                            <span />
                        )}
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                data-testid="task-cancel-button"
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={submitting} data-testid="task-save-button">
                                {submitting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : task ? (
                                    "Save changes"
                                ) : (
                                    "Create task"
                                )}
                            </Button>
                        </div>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>

        <ClientFormDialog
            open={showAddClient}
            onOpenChange={setShowAddClient}
            initialName={clientQuery.trim()}
            onSaved={onClientCreated}
        />

        <UserFormDialog
            open={showAddUser}
            onOpenChange={setShowAddUser}
            initialName={assignedQuery.trim()}
            onSaved={onUserCreated}
        />

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Delete this task?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This permanently removes the task. Audit log entries are kept.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={onDeleteTask}
                        className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                        data-testid="task-delete-confirm"
                    >
                        Delete
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
        </>
    );
}
